import { memo, useMemo } from 'react';
import '@/components/Messenger/agent/agentMessageRender/assistantParts.css';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import { remarkTightList } from '@/utils/remarkTightList';
import { normalizeMarkdown } from '@/components/Messenger/agent/agentMessageRender/normalizeMarkdown';
import { parseMessageWithButtons } from '@/components/Messenger/agent/agentMessageRender/toolFormatting';
import { MESSENGER_MARKDOWN_AGENT_FULL_COMPONENTS } from '@/components/Messenger/shared/markdown/messengerMarkdownAgentFullComponents';
import { MESSENGER_MARKDOWN_BUBBLE_TAIL_COMPONENTS } from '@/components/Messenger/shared/markdown/messengerMarkdownBubbleTailComponents';
import '@/components/Messenger/shared/markdown/messengerAgentFullMarkdown.css';

const MAX_PARSED_CACHE = 256;
const parsedMarkdownCache = new Map<string, { text: string; buttons: string[] }>();

function cacheKey(messageId: string, rawText: string) {
  return `${messageId}\u0000${rawText}`;
}

function getCachedParsed(messageId: string, rawText: string) {
  const key = cacheKey(messageId, rawText);
  const hit = parsedMarkdownCache.get(key);
  if (hit) return hit;
  const normalized = normalizeMarkdown(rawText);
  const parsed = parseMessageWithButtons(normalized);
  if (parsedMarkdownCache.size >= MAX_PARSED_CACHE) parsedMarkdownCache.clear();
  parsedMarkdownCache.set(key, parsed);
  return parsed;
}

export type MessengerMarkdownAgentExtendedProps = {
  messageId: string;
  rawText: string;
  status: string;
  sendMessage: (message: { text: string }) => void;
  /** Компактный режим: float-мета времени в одной строке с текстом. */
  bubbleTailMetaLayout?: boolean;
};

/** Расширенный markdown для входящих сообщений ассистента: GFM + кнопки из разметки. */
export const MessengerMarkdownAgentExtended = memo(function MessengerMarkdownAgentExtended({
  messageId,
  rawText,
  status,
  sendMessage,
  bubbleTailMetaLayout = false,
}: MessengerMarkdownAgentExtendedProps) {
  const { text: displayText, buttons } = useMemo(
    () => getCachedParsed(messageId, rawText),
    [messageId, rawText]
  );

  const markdownComponents = bubbleTailMetaLayout
    ? MESSENGER_MARKDOWN_BUBBLE_TAIL_COMPONENTS
    : MESSENGER_MARKDOWN_AGENT_FULL_COMPONENTS;

  const remarkPlugins = bubbleTailMetaLayout
    ? [remarkGfm, remarkTightList]
    : [remarkGfm, remarkTightList, remarkBreaks];

  return (
    <div
      className={[
        'chatAssistant__dmAssistantContentBlock',
        bubbleTailMetaLayout ? 'chatAssistant__dmAssistantContentBlock--bubbleMetaTail' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {displayText ? (
        <div
          className={[
            'chatAssistant__markdown',
            bubbleTailMetaLayout ? 'chatAssistant__markdown--bubbleMetaTail' : 'messengerAgentMd--full',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          <ReactMarkdown remarkPlugins={remarkPlugins} components={markdownComponents}>
            {displayText}
          </ReactMarkdown>
        </div>
      ) : null}
      {buttons.length > 0 ? (
        <div
          className={[
            'chatAssistant__buttons',
            bubbleTailMetaLayout ? 'chatAssistant__buttons--bubbleMetaTail' : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {buttons.map((label) => (
            <button
              key={label}
              type="button"
              className="chatAssistant__actionBtn"
              onClick={() => status === 'ready' && sendMessage({ text: label })}
              disabled={status !== 'ready'}
            >
              {label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
});
